import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { Plus, Trash2, X } from 'lucide-react';

import { FormError, FormField, FormInput } from '@/components/form';
import { Button } from '@/components/ui/button';

import type { Webhook, CreateWebhookDto, UpdateWebhookDto } from '@/lib/api/webhooks.api';

const AVAILABLE_EVENTS = [{ value: 'call.status.updated', label: 'Call Status Updated' }];

interface WebhookFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: CreateWebhookDto | UpdateWebhookDto) => Promise<void>;
  isPending: boolean;
  webhook?: Webhook | null;
}

export function WebhookFormDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  webhook,
}: WebhookFormDialogProps) {
  const isEdit = !!webhook;

  const [name, setName] = useState(webhook?.name ?? '');
  const [url, setUrl] = useState(webhook?.url ?? '');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<Set<string>>(new Set(webhook?.events ?? []));
  const [customHeaders, setCustomHeaders] = useState<{ key: string; value: string }[]>(() => {
    if (webhook?.headers) {
      return Object.entries(webhook.headers).map(([key]) => ({ key, value: '' }));
    }
    return [];
  });
  const [headersDirty, setHeadersDirty] = useState(false);
  const [active, setActive] = useState(webhook?.active ?? true);
  const [formError, setFormError] = useState('');

  const toggleEvent = (event: string) => {
    setEvents((prev) => {
      const next = new Set(prev);
      if (next.has(event)) {
        next.delete(event);
      } else {
        next.add(event);
      }
      return next;
    });
  };

  const addHeader = () => {
    setHeadersDirty(true);
    setCustomHeaders((prev) => [...prev, { key: '', value: '' }]);
  };
  const removeHeader = (index: number) => {
    setHeadersDirty(true);
    setCustomHeaders((prev) => prev.filter((_, i) => i !== index));
  };
  const updateHeader = (index: number, field: 'key' | 'value', val: string) => {
    setHeadersDirty(true);
    setCustomHeaders((prev) => prev.map((h, i) => (i === index ? { ...h, [field]: val } : h)));
  };

  const handleSubmit = async () => {
    setFormError('');

    if (!name.trim()) {
      setFormError('Name is required');
      return;
    }

    if (!url.trim()) {
      setFormError('URL is required');
      return;
    }

    try {
      new URL(url);
    } catch {
      setFormError('Please enter a valid URL');
      return;
    }

    try {
      const data: CreateWebhookDto | UpdateWebhookDto = {
        name: name.trim(),
        url: url.trim(),
        events: Array.from(events),
        active,
        ...(secret ? { secret } : {}),
      };

      if (!isEdit || headersDirty) {
        const headersWithValues = customHeaders.filter((h) => h.key.trim() && h.value.trim());
        data.headers =
          headersWithValues.length > 0
            ? Object.fromEntries(headersWithValues.map((h) => [h.key.trim(), h.value.trim()]))
            : null;
      }

      await onSubmit(data);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setFormError(err?.response?.data?.message ?? 'Failed to save webhook. Please try again.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-slate-900">
          <Dialog.Title className="mb-2 text-lg font-semibold">
            {isEdit ? 'Edit Webhook' : 'Create Webhook'}
          </Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            {isEdit
              ? 'Update the webhook configuration.'
              : 'Configure a new webhook endpoint to receive event notifications.'}
          </Dialog.Description>

          <div className="space-y-4">
            <FormField label="Name" htmlFor="webhook-name" required error={undefined}>
              <FormInput
                id="webhook-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Production Notifier"
              />
            </FormField>

            <FormField label="URL" htmlFor="webhook-url" required error={undefined}>
              <FormInput
                id="webhook-url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/webhook"
              />
            </FormField>

            <FormField
              label="Secret"
              htmlFor="webhook-secret"
              description={
                isEdit
                  ? 'Leave blank to keep the existing secret. Enter a new value to replace it.'
                  : 'Used to sign payloads with HMAC-SHA256 (optional).'
              }
              error={undefined}
            >
              <FormInput
                id="webhook-secret"
                type="password"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                placeholder={isEdit ? '(unchanged)' : 'Optional signing secret'}
              />
            </FormField>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Custom Headers</p>
                  <p className="text-muted-foreground text-xs">
                    Add headers like X-API-Key, Authorization, etc.
                  </p>
                </div>
                <Button type="button" variant="outline" size="sm" onClick={addHeader}>
                  <Plus className="mr-1 h-3 w-3" />
                  Add
                </Button>
              </div>
              {customHeaders.map((header, index) => (
                <div key={index} className="mb-2 flex items-center gap-2">
                  <FormInput
                    placeholder="Header name"
                    value={header.key}
                    onChange={(e) => updateHeader(index, 'key', e.target.value)}
                    className="flex-1"
                  />
                  <FormInput
                    type="password"
                    autoComplete="new-password"
                    placeholder={isEdit ? '(unchanged)' : 'Value'}
                    value={header.value}
                    onChange={(e) => updateHeader(index, 'value', e.target.value)}
                    className="flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => removeHeader(index)}
                    aria-label={header.key ? `Remove header ${header.key}` : 'Remove header'}
                    className="text-muted-foreground hover:text-destructive shrink-0 p-1"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            <div>
              <p className="mb-2 text-sm font-medium">Events</p>
              <p className="text-muted-foreground mb-2 text-xs">
                Subscribe to automatic events. Leave empty if you only plan to trigger manually.
              </p>
              <div className="space-y-2">
                {AVAILABLE_EVENTS.map((evt) => (
                  <label
                    key={evt.value}
                    className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
                  >
                    <input
                      type="checkbox"
                      checked={events.has(evt.value)}
                      onChange={() => toggleEvent(evt.value)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                    />
                    <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">
                      {evt.value}
                    </code>
                    <span className="text-muted-foreground">- {evt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
              />
              <span className="font-medium">Active</span>
              <span className="text-muted-foreground">
                - Webhook will receive events when active
              </span>
            </label>

            {formError && <FormError message={formError} />}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? 'Saving...' : isEdit ? 'Update Webhook' : 'Create Webhook'}
            </Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
